// EL MES — ¿llego a lo del mes pasado? ¿ya cubrí los gastos?
//
// Cuatro líneas de texto. **Sin gráfico**, y es a propósito: la respuesta a
// estas dos preguntas son números y una frase, no una curva. Un gráfico acá
// sería decoración que hay que interpretar.
//
// Cuatro reglas de redacción que este bloque no puede romper:
//
// · PROHIBIDO EL "~". Si es estimado se escribe *estimado*. El "~" es un
//   símbolo que la mitad de la gente no lee como "aproximado", y la otra mitad
//   lo lee y no sabe cuánto de aproximado.
//
// · LA PROYECCIÓN ES UN RANGO, NO UN NÚMERO. La fórmula tiene ~18% de error
//   medido, así que un monto exacto sería precisión fingida. El margen se
//   calibra con la historia del propio cliente (ver proyeccion.js).
//
// · EL TITULAR ES LA CONCLUSIÓN, no la categoría. Lo que el dueño vino a saber
//   es si llega a lo del mes pasado — eso va arriba, y el rango es la prueba.
//
// SE QUITÓ el campo de gastos fijos, que pedía cargar cuánto cuesta el mes para
// decir qué día quedó cubierto. La idea es buena —"cubriste tus gastos el día
// 14" es un número que el dueño hoy no tiene— pero esta versión no se la ganaba:
// era lo único de la pantalla que PEDÍA antes de dar, la pregunta era ambigua, y
// sólo se guardaba en ese navegador, tanto que había que advertirlo en pantalla.
// Vuelve cuando se guarde junto a la configuración del estacionamiento y viaje
// entre dispositivos.

import React, { useMemo } from "react";
import Bloque from "./Bloque";
import { UI, POLARIDAD } from "./theme";
import { formatCurrency, mesLargo } from "./format";
import { proyeccionMes } from "./proyeccion";

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Una línea: rótulo, monto, y la aclaración que hace que el monto se entienda.
 *
 * El rótulo lleva `flex: "0 0 auto"` con `minWidth`, y NO `flex: "0 0 130px"`.
 * En un contenedor flex en COLUMNA —que es lo que pasa en el teléfono con
 * `flex-column flex-sm-row`— `flex-basis` mide la ALTURA, no el ancho: el
 * rótulo se volvía una caja de 130 px de alto y dejaba un hueco enorme antes
 * del número. Con `auto` + `minWidth` da el mismo ancho de columna en
 * escritorio y se ajusta al contenido en el teléfono.
 */
const Linea = ({ rotulo, monto, nota, color }) => (
  // En el teléfono el monto baja a su propia línea: en 320 px la columna
  // derecha parte el rótulo en dos y queda ilegible.
  <div
    className="d-flex flex-column flex-sm-row"
    style={{ padding: "9px 0", borderBottom: `1px solid ${UI.linea}`, gap: 2, alignItems: "baseline" }}
  >
    <div style={{ flex: "0 0 auto", minWidth: 130, fontSize: 12.5, color: UI.textoSuave, textTransform: "uppercase", letterSpacing: 0.4 }}>
      {rotulo}
    </div>
    <div style={{ flex: "0 0 auto", minWidth: 170 }}>
      <span style={{ fontSize: 19, fontWeight: 700, color: color || UI.texto }}>{monto}</span>
    </div>
    {nota && <div style={{ flex: "1 1 auto", fontSize: 12.5, color: UI.textoSuave, lineHeight: 1.4 }}>{nota}</div>}
  </div>
);

const ElMes = ({ cerrados = [], hoy = null, mensuales = [], companyId }) => {
  const iso = hoyISO();
  const p = useMemo(() => proyeccionMes(cerrados, hoy, iso, mensuales), [cerrados, hoy, mensuales, iso]);

  // mesLargo devuelve "Agosto 2026". En el titular el año sobra —se está
  // hablando del mes en curso— y con el año suena a documento, no a alguien
  // contándote cómo viene el mes.
  const nombreMes = mesLargo(iso.slice(0, 7)).replace(/\s+\d{4}$/, "");

  // ── El titular: la conclusión ─────────────────────────────────────────
  let titular;
  let frase = null;
  if (p.terminado) {
    titular = `${nombreMes} cerró en ${formatCurrency(p.llevas)}`;
    if (p.contraMesPasado === "arriba") frase = "Arriba del mes pasado.";
    if (p.contraMesPasado === "abajo") frase = "Abajo del mes pasado.";
  } else if (p.banda && p.contraMesPasado) {
    titular = {
      arriba: `${nombreMes} va a terminar arriba del mes pasado`,
      abajo: `${nombreMes} va a terminar abajo del mes pasado`,
      parecido: `${nombreMes} va a terminar parecido al mes pasado`,
    }[p.contraMesPasado];
    // Las tres frases dicen lo mismo que decía la versión anterior, pero sin
    // hablar en estadístico. "La estimación entera queda de ese lado, no es una
    // diferencia de margen" es exacto y no se entiende: ni "queda de ese lado"
    // ni "margen" significan nada para el dueño de una playa.
    //
    // Lo que hay que transmitir es POR QUÉ se puede afirmar: aun en el peor
    // escenario del cálculo, el resultado no cambia de lado. Eso se dice
    // poniendo el escenario y el número que el dueño reconoce.
    frase = {
      arriba: `Aun si el resto del mes te viene flojo, terminas arriba de ${formatCurrency(p.mesPasado)}.`,
      abajo: `Aun si el resto del mes te viene bien, no llegas a ${formatCurrency(p.mesPasado)}.`,
      // "Parecido" no es una evasiva: es lo único que se puede afirmar cuando
      // el mes pasado cae dentro de lo que este cálculo puede distinguir.
      parecido: "Está tan cerca que todavía no se puede decir para qué lado va a terminar.",
    }[p.contraMesPasado];
  } else if (p.banda) {
    titular = `Vas a terminar ${nombreMes} entre ${formatCurrency(p.banda.bajo)} y ${formatCurrency(p.banda.alto)}`;
  } else {
    titular = `Llevas ${formatCurrency(p.llevas)} en ${nombreMes}`;
    frase = p.diaDeHoy < 10
      ? "Con menos de diez días es muy temprano para estimar cómo va a terminar: el rango sería tan ancho que no diría nada."
      : "Todavía no hay meses anteriores completos para estimar cómo va a terminar.";
  }

  return (
    <Bloque
      nombre="Cómo termina el mes"
      id="el-mes"
      titular={titular}
      frase={frase}
      rango={`${nombreMes} · ${p.diasOperados} ${p.diasOperados === 1 ? "día" : "días"} con movimiento de ${p.diaDeHoy} transcurridos`}
    >
      <div>
        <Linea
          rotulo="Llevas"
          monto={formatCurrency(p.llevas)}
          nota={
            p.diasOperados !== p.diaDeHoy
              // Días con movimiento y días transcurridos no son lo mismo, y
              // confundirlos es lo que hace que un "30d" sean 35 días.
              ? `en ${p.diasOperados} días con movimiento, de ${p.diaDeHoy} transcurridos`
              : `${p.diaDeHoy} de ${p.diasDelMes} días`
          }
        />

        {p.banda && (
          <Linea
            rotulo="Vas a terminar"
            monto={`${formatCurrency(p.banda.bajo)} a ${formatCurrency(p.banda.alto)}`}
            // Un rango YA dice que es incierto: no hace falta explicar de donde
            // sale el ancho. La version anterior contaba el metodo ("el rango sale
            // de cuanto se equivoco esta misma cuenta en tus 12 meses anteriores")
            // y era exacta y opaca a la vez.
            nota="estimado, según lo que llevas hasta hoy"
          />
        )}

        {p.mesPasado !== null && (
          <Linea
            rotulo="Mes pasado"
            monto={formatCurrency(p.mesPasado)}
            nota={
              <>
                {p.contraMesPasado === "parecido" ? (
                  <span>muy parecido a lo que estás por terminar</span>
                ) : p.variacionCentral !== null ? (
                  // El porcentaje describe a la ESTIMACIÓN, no al mes pasado,
                  // pero vive en el renglón del mes pasado. Puesto suelto
                  // ("$3.010.500  +198%") se lee como si el mes pasado hubiera
                  // crecido 198%, que es lo contrario de lo que dice. Por eso la
                  // frase empieza nombrando el sujeto.
                  <span style={{ fontWeight: 400, color: UI.textoSuave }}>
                    {p.terminado ? "cerraste " : "la estimación queda "}
                    <span style={{ color: p.variacionCentral >= 0 ? POLARIDAD.entra.color : POLARIDAD.sale.color, fontWeight: 600 }}>
                      {Math.abs(p.variacionCentral).toFixed(0)}% {p.variacionCentral >= 0 ? "arriba" : "abajo"}
                    </span>
                    {" de esto"}
                  </span>
                ) : null}
                {p.mesPasadoParcial && (
                  // El Desktop consolida el mes entre el día 1 y el 3. En el
                  // medio esto es una suma de diarios y puede faltar alguno.
                  <span> · sumado día por día, porque el resumen del mes todavía no llegó</span>
                )}
              </>
            }
          />
        )}

      </div>
    </Bloque>
  );
};

export default ElMes;
